// src/utils/codebookUtils.ts

import { CodebookData, AntTreeNode } from "types/codebook.types"
import {
  SelectOption,
  GroupingFlatOption,
  AntOptGroup,
  OperatorOption,
} from "types/ui.types"
import { HierarchicalDataEntry } from "src/types/engineOutput.types"
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"

/**
 * Transforms the codebook data into a tree structure suitable for the Ant Design Tree component.
 *
 * `title` MUST stay a plain string — consumers join it into the Data(Q)
 * `<<path//to//node>>` syntax (drag/select handlers via
 * `getCodebookNodePath`) and `filterCodebookTree` does a `toLowerCase`
 * match against it. Decoration (type-tag pills, icons) belongs in the
 * Tree's `titleRender`/`icon` props, using `originalData` where needed.
 */
export const transformCodebookToTreeData = (
  data: CodebookData | string | number | boolean | null,
  parentKey: string = ""
): AntTreeNode[] => {
  if (Array.isArray(data)) {
    // TypeScript now knows 'data' is HierarchicalDataEntry[] if it's an array from CodebookData
    return (data as HierarchicalDataEntry[]).map(
      (entry: HierarchicalDataEntry, index: number) => {
        const { label, type, value } = entry // value is HierarchicalDataEntry[] | primitive[] | undefined
        const currentKey = parentKey ? `${parentKey}-${index}` : `${index}`
        return {
          title: label,
          key: currentKey,
          isLeaf: type !== "TABLE",
          children:
            type === "TABLE" && value
              ? transformCodebookToTreeData(
                  value as HierarchicalDataEntry[],
                  currentKey
                ) // value is compatible
              : undefined,
          originalData: entry,
        }
      }
    )
  } else if (typeof data === "object" && data !== null) {
    // This is CodebookObject
    return Object.entries(data).map(
      (
        [key, valueNode]: [
          string,
          CodebookData | string | number | boolean | null
        ],
        index: number
      ) => {
        const currentKey = parentKey ? `${parentKey}-${index}` : `${index}`
        const isLeafNode = typeof valueNode !== "object" || valueNode === null
        return {
          title: key,
          key: currentKey,
          isLeaf: isLeafNode,
          children: !isLeafNode
            ? transformCodebookToTreeData(valueNode, currentKey)
            : undefined,
        }
      }
    )
  }
  return []
}

/**
 * Resolves a tree node's key (the `parentIdx-childIdx-…` chain produced by
 * `transformCodebookToTreeData`) back into the list of node titles from the
 * root down — i.e. the segments of the node's Data(Q) `//` path.
 * Returns `[]` when the key doesn't resolve.
 */
export const getCodebookNodePath = (
  treeData: AntTreeNode[],
  key: string
): string[] => {
  const keys = key.split("-")
  const path: string[] = []

  const traverse = (nodes: AntTreeNode[], depth: number): boolean => {
    for (const node of nodes) {
      if (node.key === keys.slice(0, depth + 1).join("-")) {
        path.push(node.title)
        if (depth === keys.length - 1) {
          return true
        }
        if (node.children) {
          return traverse(node.children, depth + 1)
        }
      }
    }
    return false
  }

  traverse(treeData, 0)
  return path
}

/**
 * Filters a codebook tree by a case-insensitive title match. A node is kept
 * when its own title matches, or (pruned to the matching subtree) when any
 * descendant matches. An empty search term returns the tree unchanged.
 */
export const filterCodebookTree = (
  treeData: AntTreeNode[],
  searchTerm: string
): AntTreeNode[] => {
  if (!searchTerm) return treeData

  const filterNode = (node: AntTreeNode): AntTreeNode | false => {
    if (node.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return node
    }
    if (node.children) {
      const filteredChildren = node.children
        .map(filterNode)
        .filter((child): child is AntTreeNode => child !== false)
      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren }
      }
    }
    return false
  }

  return treeData
    .map(filterNode)
    .filter((node): node is AntTreeNode => node !== false)
}

/**
 * Flattens the codebook data into a format suitable for Ant Design Select components.
 */
export const flattenCodebookForSelect = (
  data: CodebookData | string | number | boolean | null,
  parentPath: string = "",
  addedPaths: Set<string> = new Set()
): SelectOption[] => {
  let options: SelectOption[] = []
  if (!data) return options

  if (Array.isArray(data)) {
    // data is HierarchicalDataEntry[]
    if (parentPath && !addedPaths.has(parentPath)) {
      options.push({ value: parentPath, label: parentPath })
      addedPaths.add(parentPath)
    }
    ;(data as HierarchicalDataEntry[]).forEach(
      (item: HierarchicalDataEntry) => {
        // Validate item before processing
        if (
          !item ||
          typeof item !== "object" ||
          typeof item.label === "undefined"
        ) {
          console.warn("Skipping invalid item in codebook array:", item)
          return
        }

        // item is HierarchicalDataEntry
        const currentPath = parentPath
          ? `${parentPath}//${item.label}`
          : String(item.label)
        if (item.type !== "TABLE") {
          options.push({ value: currentPath, label: currentPath })
        }
        if (item.type === "TABLE" && item.value) {
          // item.value is HierarchicalDataEntry[] or primitive array
          options = options.concat(
            flattenCodebookForSelect(
              item.value as HierarchicalDataEntry[],
              currentPath,
              addedPaths
            )
          )
        }
      }
    )
  } else if (typeof data === "object" && data !== null) {
    // data is CodebookObject
    if (parentPath && !addedPaths.has(parentPath)) {
      options.push({ value: parentPath, label: parentPath })
      addedPaths.add(parentPath)
    }
    Object.entries(data).forEach(
      ([key, valueNode]: [
        string,
        CodebookData | string | number | boolean | null
      ]) => {
        const currentPath = parentPath ? `${parentPath}//${key}` : key
        if (typeof valueNode !== "object" || valueNode === null) {
          options.push({ value: currentPath, label: currentPath })
        } else {
          options = options.concat(
            flattenCodebookForSelect(valueNode, currentPath, addedPaths)
          )
        }
      }
    )
  }
  return options
}

/**
 * Returns the labels of a container's direct BOOL children — the "categories"
 * a CAT step maps into. Walks the codebook (an array of HierarchicalDataEntry)
 * down the `//`-separated container path, then collects each child whose type
 * is BOOL. Returns `[]` when the container isn't found or has no BOOL children
 * (e.g. it hasn't been applied to the engine session yet).
 */
export const getBoolChildLabels = (
  codebook: CodebookData | null | undefined,
  containerPath: string | null | undefined
): string[] => {
  if (!codebook || !containerPath || !Array.isArray(codebook)) return []
  const parts = containerPath
    .split("//")
    .map((p) => p.trim())
    .filter(Boolean)
  let current = codebook as HierarchicalDataEntry[]
  for (const part of parts) {
    const node = current.find((e) => e && e.label === part)
    if (!node || node.type !== "TABLE" || !Array.isArray(node.value)) {
      return []
    }
    current = node.value as HierarchicalDataEntry[]
  }
  return current
    .filter(
      (e): e is HierarchicalDataEntry =>
        !!e && e.type === "BOOL" && typeof e.label === "string"
    )
    .map((e) => e.label)
}

/**
 * Flattens the codebook data into a format suitable for grouping in Ant Design Select components.
 */
const flattenCodebookRecursiveForGrouping = (
  data: CodebookData | string | number | boolean | null,
  parentPath: string = ""
): GroupingFlatOption[] => {
  let options: GroupingFlatOption[] = []
  if (!data) return options

  if (Array.isArray(data)) {
    // data is HierarchicalDataEntry[]
    ;(data as HierarchicalDataEntry[]).forEach(
      (item: HierarchicalDataEntry) => {
        // Validate item before processing
        if (
          !item ||
          typeof item !== "object" ||
          typeof item.label === "undefined"
        ) {
          console.warn("Skipping invalid item in codebook array:", item)
          return
        }

        const currentPath = parentPath
          ? `${parentPath}//${item.label}`
          : String(item.label)
        const currentParent = parentPath || undefined
        if (item.type !== "TABLE") {
          options.push({
            value: currentPath,
            label: String(item.label),
            parent: currentParent,
          })
        }
        if (item.type === "TABLE" && item.value) {
          options = options.concat(
            flattenCodebookRecursiveForGrouping(
              item.value as HierarchicalDataEntry[],
              currentPath
            )
          )
        }
      }
    )
  } else if (typeof data === "object" && data !== null) {
    // data is CodebookObject
    Object.entries(data).forEach(
      ([key, valueNode]: [
        string,
        CodebookData | string | number | boolean | null
      ]) => {
        const currentPath = parentPath ? `${parentPath}//${key}` : key
        const currentParent = parentPath || undefined
        if (typeof valueNode !== "object" || valueNode === null) {
          options.push({
            value: currentPath,
            label: key,
            parent: currentParent,
          })
        } else {
          options = options.concat(
            flattenCodebookRecursiveForGrouping(valueNode, currentPath)
          )
        }
      }
    )
  }
  return options
}

/**
 * Groups the flattened codebook options into Ant Design OptGroup format.
 */
export const groupCodebookOptions = (
  codebookData: CodebookData | null | undefined
): AntOptGroup[] => {
  if (!codebookData) {
    return []
  }
  try {
    const flatOptions = flattenCodebookRecursiveForGrouping(codebookData)
    const grouped: { [groupLabel: string]: SelectOption[] } = {}

    flatOptions.forEach((option: GroupingFlatOption) => {
      const { value, label, parent } = option
      const groupLabel = parent ? parent.split("//")[0] : "Other"

      if (!grouped[groupLabel]) {
        grouped[groupLabel] = []
      }

      let displayLabel = label
      if (parent && parent.includes("//")) {
        const parentParts = parent.split("//")
        if (parentParts[0] === groupLabel && parentParts.length > 1) {
          displayLabel = `${parentParts.slice(1).join("//")}//${label}`
        } else if (parentParts[0] !== groupLabel && groupLabel !== "Other") {
          displayLabel = `${parent}//${label}`
        }
      }
      grouped[groupLabel].push({
        value: value,
        label: displayLabel,
      })
    })

    return Object.entries(grouped).map(
      ([group, opts]: [string, SelectOption[]]) => ({
        label: group,
        options: opts,
      })
    )
  } catch (error) {
    console.error("Error processing codebook for grouping:", error)
    return []
  }
}

// ============================================================================
// NEW: Variable Type Detection and Operation Type Utilities
// ============================================================================

/**
 * Extracts variable type from codebook structure
 * @param codebook - The codebook data structure
 * @param variableName - Variable name to look up (can include // path notation)
 * @returns The type string ("INT", "BOOL", "TABLE", etc.) or null if not found
 */
export const getVariableType = (
  codebook: CodebookData | null | undefined,
  variableName: string | null | undefined
): string | null => {
  if (!codebook || !variableName) return null

  // Function to recursively search through codebook structure
  const findVariableType = (
    data: CodebookData | string | number | boolean | null,
    varName: string
  ): string | null => {
    if (Array.isArray(data)) {
      // data is HierarchicalDataEntry[]
      for (const item of data as HierarchicalDataEntry[]) {
        // Check if this item matches our variable
        if (item.label === varName) {
          return item.type
        }

        // If this is a TABLE type with nested values, search within
        if (item.type === "TABLE" && item.value) {
          const result = findVariableType(
            item.value as HierarchicalDataEntry[],
            varName
          )
          if (result) return result
        }

        // Check if varName includes path notation and matches current path
        if (varName.includes("//")) {
          const pathParts = varName.split("//")
          if (pathParts[0] === String(item.label) && pathParts.length > 1) {
            const remainingPath = pathParts.slice(1).join("//")
            if (item.type === "TABLE" && item.value) {
              const result = findVariableType(
                item.value as HierarchicalDataEntry[],
                remainingPath
              )
              if (result) return result
            }
          }
        }
      }
    } else if (typeof data === "object" && data !== null) {
      // data is CodebookObject
      for (const [key, valueNode] of Object.entries(data)) {
        // Direct match
        if (key === varName) {
          // For object entries, we need to determine the type
          if (typeof valueNode === "object" && valueNode !== null) {
            return "TABLE" // Nested object
          } else {
            return "BOOL" // Primitive value (default to BOOL for backwards compatibility)
          }
        }

        // Path notation match
        if (varName.includes("//")) {
          const pathParts = varName.split("//")
          if (pathParts[0] === key && pathParts.length > 1) {
            const remainingPath = pathParts.slice(1).join("//")
            if (typeof valueNode === "object" && valueNode !== null) {
              const result = findVariableType(valueNode, remainingPath)
              if (result) return result
            }
          }
        }

        // Recursive search in nested objects
        if (typeof valueNode === "object" && valueNode !== null) {
          const result = findVariableType(valueNode, varName)
          if (result) return result
        }
      }
    }

    return null
  }

  return findVariableType(codebook, variableName)
}

/**
 * Checks if any variables in the provided list are INT type
 * @param codebook - The codebook data structure
 * @param variables - Array of variable names to check
 * @returns True if any variable is INT type
 */
export const hasIntVariables = (
  codebook: CodebookData | null | undefined,
  variables: (string | null | undefined)[]
): boolean => {
  if (!variables || variables.length === 0) return false

  return variables.some((variable) => {
    if (!variable) return false
    const type = getVariableType(codebook, variable)
    return type === "INT"
  })
}

/**
 * Determines whether to use DTAB or XTAB based on variable types
 * @param codebook - The codebook data structure
 * @param variables - Array of variable names to check
 * @returns "DTAB" if any variable is INT type, "XTAB" otherwise
 */
export const determineOperationType = (
  codebook: CodebookData | null | undefined,
  variables: (string | null | undefined)[] = []
): "DTAB" | "XTAB" => {
  // Rule: If any variable is INT type, use DTAB. Otherwise use XTAB.
  if (hasIntVariables(codebook, variables)) {
    return "DTAB"
  }

  return "XTAB"
}

/**
 * Gets available statistic options based on variable types
 * @param codebook - The codebook data structure
 * @param variables - Array of variable names to check
 * @returns Array of available statistic options with value and label
 */
export const getAvailableStatisticOptions = (
  codebook: CodebookData | null | undefined,
  variables: (string | null | undefined)[] = []
): OperatorOption[] => {
  const hasInt = hasIntVariables(codebook, variables)

  if (hasInt) {
    // INT fields in DTAB - AVG is available
    return [
      { value: STATISTIC_TYPE.BASE_N, label: "base n" },
      { value: STATISTIC_TYPE.AVG, label: "avg" },
      { value: STATISTIC_TYPE.MIN, label: "min" },
      { value: STATISTIC_TYPE.MAX, label: "max" },
      { value: STATISTIC_TYPE.CRR_PERCENT, label: "CRR%" },
    ]
  } else {
    // BOOL and other non-INT types in XTAB - NO AVG
    return [
      { value: STATISTIC_TYPE.N, label: "Count (n)" },
      { value: STATISTIC_TYPE.BIG_N, label: "Weighted Count (N)" },
      { value: STATISTIC_TYPE.ROW_PERCENT, label: "Row %" },
      { value: STATISTIC_TYPE.COL_PERCENT, label: "Col %" },
      { value: STATISTIC_TYPE.INDEX, label: "Index" },
      { value: STATISTIC_TYPE.PERC_PERCENT, label: "Percentage %" },
    ]
  }
}

/**
 * Updates selected operators to ensure they're valid for the determined operation type
 * @param currentOperators - Currently selected operators
 * @param availableOptions - Available statistic options based on variable types
 * @returns Filtered/updated operators array
 */
export const updateOperatorsForAvailableOptions = (
  currentOperators: string[],
  availableOptions: { value: string; label: string }[]
): string[] => {
  const availableValues = availableOptions.map((opt) => opt.value)
  const validOperators = currentOperators.filter((op) =>
    availableValues.includes(op)
  )

  // If no valid operators remain, select the first available
  if (validOperators.length === 0 && availableOptions.length > 0) {
    return [availableOptions[0].value]
  }

  return validOperators.length > 0 ? validOperators : currentOperators
}

/**
 * Helper function to get display label for table operator options
 * @param statisticType - The statistic type value
 * @returns Display label for the statistic type
 */
export const getTableOperatorLabel = (statisticType: string): string => {
  switch (statisticType) {
    case STATISTIC_TYPE.BASE_N:
      return "base n"
    case STATISTIC_TYPE.N:
      return "n"
    case STATISTIC_TYPE.PERC:
      return "perc"
    case STATISTIC_TYPE.CRR_PERCENT:
      return "CRR%"
    case STATISTIC_TYPE.AVG:
      return "avg"
    case STATISTIC_TYPE.MIN:
      return "min"
    case STATISTIC_TYPE.MAX:
      return "max"
    case STATISTIC_TYPE.ROW_PERCENT:
      return "Row %"
    case STATISTIC_TYPE.COL_PERCENT:
      return "Col %"
    case STATISTIC_TYPE.INDEX:
      return "Index"
    case STATISTIC_TYPE.PERC_PERCENT:
      return "perc%"
    case STATISTIC_TYPE.BIG_N:
      return "N (Weighted)"
    default:
      return statisticType
  }
}
